import * as THREE from 'three';
import { KIND_COLOR, KINDS, type Kind } from '../game/config';
import { DirtyRange, FOG_FRAG, FOG_VERT, fxUniforms, quadGeometry } from './common';

/** Four elemental silhouettes in one pooled draw. Only live transforms upload;
 * slots and materials are reused, and an exhausted visual pool never drops damage.
 */
export class Projectiles {
  readonly mesh: THREE.Mesh;
  private readonly position: THREE.InstancedBufferAttribute;
  private readonly motion: THREE.InstancedBufferAttribute;
  private readonly style: THREE.InstancedBufferAttribute;
  private readonly moving = new DirtyRange();
  private readonly styling = new DirtyRange();
  private readonly active: Uint8Array;
  private readonly free: number[];
  private count = 0;

  constructor(readonly capacity = 128) {
    const g = quadGeometry();
    const attribute = (name: string) => {
      const a = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4).setUsage(THREE.DynamicDrawUsage);
      g.setAttribute(name, a); return a;
    };
    this.position = attribute('iPosition'); this.motion = attribute('iMotion'); this.style = attribute('iStyle');
    this.active = new Uint8Array(capacity);
    this.free = Array.from({ length: capacity }, (_, i) => capacity - 1 - i);
    g.instanceCount = 0;
    this.mesh = new THREE.Mesh(g, new THREE.ShaderMaterial({
      uniforms: { ...fxUniforms, uColors: { value: KINDS.map(k => new THREE.Color(KIND_COLOR[k])) } },
      transparent: true, depthWrite: false,
      vertexShader: /* glsl */ `
        attribute vec4 iPosition, iMotion, iStyle;
        uniform float uTime;
        varying vec2 vUv; varying float vKind, vAge, vGrade;
        ${FOG_VERT}
        void main() {
          if (iPosition.w < 0.5) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
          vUv = uv * 2.0 - 1.0; vKind = iStyle.y; vAge = uTime - iStyle.z; vGrade = iStyle.w;
          vec4 mv = modelViewMatrix * vec4(iPosition.xyz, 1.0);
          vec2 velocity = (viewMatrix * vec4(iMotion.xyz, 0.0)).xy;
          float speed = length(velocity);
          vec2 dir = speed > 0.001 ? velocity / speed : vec2(0.0, 1.0);
          vec2 side = vec2(dir.y, -dir.x);
          float aspect = vKind < 0.5 ? 1.8 : vKind < 1.5 ? 3.1 : vKind < 2.5 ? 3.6 : 1.3;
          float pulse = vKind > 2.5 ? 1.0 + sin(vAge * 14.0) * 0.055 : 1.0;
          mv.xy += (side * position.x + dir * position.y * aspect) * iStyle.x * pulse;
          FOG_FROM_MV(mv)
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        uniform float uTime; uniform vec3 uColors[${KINDS.length}];
        varying vec2 vUv; varying float vKind, vAge, vGrade;
        ${FOG_FRAG}
        void main() {
          vec2 q = vUv;
          float a; vec3 c;
          if (vKind < 0.5) {
            // Ember: asymmetric molten head and three licking tongues behind it.
            float tongues = sin(q.y * 15.0 + vAge * 32.0) * 0.065 + sin(q.y * 25.0 - vAge * 21.0) * 0.035;
            float width = (0.48 + tongues) * (0.72 - q.y * 0.3);
            float d = length(vec2(q.x / width, (q.y + 0.04) * 1.05));
            a = 1.0 - smoothstep(0.75, 1.05, d);
            float core = exp(-dot(q * vec2(4.0, 3.0), q * vec2(4.0, 3.0)));
            c = mix(uColors[0] * 1.7, vec3(3.4, 1.85, 0.45), core);
            c += uColors[0] * (0.25 + 0.2 * sin(q.y * 20.0 + vAge * 30.0));
          } else if (vKind < 1.5) {
            // Frost: a long hard-edged crystal, dark facets and a thin bright spine.
            float d = abs(q.x) * 1.65 + abs(q.y);
            a = 1.0 - smoothstep(0.95, 1.02, d);
            float face = q.x > 0.0 ? 1.1 : 0.38;
            c = uColors[1] * face + vec3(0.06, 0.14, 0.2);
            c += vec3(0.5, 1.0, 1.3) * pow(1.0 - abs(q.x), 18.0);
            c += vec3(0.25, 0.7, 1.0) * smoothstep(0.84, 0.97, d);
          } else if (vKind < 2.5) {
            // Sun: a narrow golden lance with a small four-point discharge core.
            float d = abs(q.x) * 3.0 + abs(q.y);
            float lance = 1.0 - smoothstep(0.86, 1.04, d);
            float cross = exp(-abs(q.y) * 27.0) * exp(-abs(q.x) * 4.5);
            a = max(lance, cross * 0.8);
            c = mix(uColors[2] * 2.0, vec3(3.2, 2.55, 1.1), exp(-abs(q.x) * 20.0));
          } else if (vKind > 9.5) {
            // Storm eye: a cyan eye in a wheeling cloud ring, a white pupil.
            float d = length(q), ang = atan(q.y, q.x);
            float arms = pow(max(0.0, cos(ang * 2.0 - d * 9.0 + vAge * 16.0)), 4.0) * smoothstep(0.2, 0.35, d) * (1.0 - smoothstep(0.75, 0.9, d));
            float eye = exp(-d * d * 30.0);
            a = max(arms * 0.9, eye);
            c = uColors[10] * (0.6 + arms * 1.6) + vec3(2.2, 2.8, 3.0) * eye;
          } else if (vKind > 8.5) {
            // Obsidian: a black glass shard, an ember crack down its spine.
            float d = abs(q.x) * 2.1 + abs(q.y);
            a = 1.0 - smoothstep(0.9, 1.0, d);
            float crack = exp(-abs(q.x + sin(q.y * 9.0) * 0.06) * 28.0);
            c = vec3(0.03, 0.025, 0.03) + uColors[9] * crack * 2.2 + vec3(0.4, 0.3, 0.3) * smoothstep(0.82, 0.97, d);
          } else if (vKind > 7.5) {
            // Onyx: a black pearl with a turning pink accretion ring.
            float d = length(q), ang = atan(q.y, q.x);
            float ring = exp(-pow((d - 0.62) * 9.0, 2.0)) * (0.6 + 0.4 * sin(ang * 3.0 - vAge * 14.0));
            a = max(1.0 - smoothstep(0.42, 0.5, d), ring) * 0.95;
            c = uColors[8] * ring * 2.0 + vec3(0.02, 0.0, 0.02);
          } else if (vKind > 6.5) {
            // Summoned sundial: a brass medallion with twelve ticks and a fixed needle.
            float d = length(q), angle = atan(q.y, q.x);
            float rim = exp(-pow((d - 0.73) * 30.0, 2.0));
            float ticks = pow(max(0.0, cos(angle * 12.0)), 18.0) * smoothstep(0.46, 0.55, d) * (1.0 - smoothstep(0.64, 0.7, d));
            float hand = pow(max(0.0, cos(angle - 0.7)), 65.0) * (1.0 - smoothstep(0.45, 0.57, d));
            a = (1.0 - smoothstep(0.79, 0.87, d)) * 0.9;
            c = uColors[7] * (0.17 + rim * 1.6 + ticks) + vec3(1.8, 1.4, 0.7) * hand;
          } else if (vKind > 5.5) {
            // Summoned garnet: a wine-red tear and a pink edge, rather than the prism's rainbow.
            float d = length(q * vec2(1.15 + q.y * 0.35, 1.0));
            float rim = exp(-pow((d - 0.7) * 16.0, 2.0));
            float heart = exp(-dot(q, q) * 8.0) * (0.8 + 0.2 * sin(vAge * 22.0));
            a = (1.0 - smoothstep(0.84, 0.96, d)) * 0.95;
            c = uColors[6] * (0.22 + rim * 1.3 + heart) + vec3(0.3, 0.005, 0.02);
          } else if (vKind > 4.5) {
            // Prism: a clear diamond of light splitting into a slowly turning rainbow.
            float d = abs(q.x) * 1.3 + abs(q.y);
            a = (1.0 - smoothstep(0.86, 1.0, d)) * 0.9;
            float hue = atan(q.y, q.x) / 6.2832 + vAge * 0.6;
            c = (0.5 + 0.5 * cos(6.2832 * (hue + vec3(0.0, 0.33, 0.67)))) * 1.4 + vec3(1.6) * exp(-d * d * 9.0);
          } else if (vKind > 3.5) {
            // Arcane: a violet four-point star with a white heart, slowly turning.
            float t = vAge * 4.0, cs = cos(t), sn = sin(t);
            vec2 r = vec2(cs * q.x - sn * q.y, sn * q.x + cs * q.y);
            float star = exp(-abs(r.x * r.y) * 60.0) * (1.0 - smoothstep(0.4, 1.0, length(r)));
            float core = exp(-dot(q, q) * 14.0);
            a = max(star, core);
            c = uColors[4] * (1.2 + star * 1.6) + vec3(2.4, 2.0, 2.8) * core;
          } else {
            // Venom: translucent dark membrane, lime rim and orbiting bubbles.
            q.x += sin(q.y * 8.0 + vAge * 12.0) * 0.055;
            float d = length(q * vec2(1.22, 1.06));
            a = (1.0 - smoothstep(0.8, 0.99, d)) * 0.92;
            float rim = exp(-pow((d - 0.73) * 12.0, 2.0));
            float bubbles = 0.0;
            for (int i = 0; i < 3; i++) {
              float angle = float(i) * 2.094 + vAge * 3.0;
              vec2 center = vec2(cos(angle), sin(angle)) * 0.36;
              bubbles += exp(-dot(q - center, q - center) * 100.0);
            }
            // a glowing toxic heart pulsing inside the dark membrane
            float heart = exp(-d * d * 7.0) * (0.85 + 0.15 * sin(vAge * 18.0));
            c = uColors[3] * (0.23 + rim * 1.8 + bubbles * 0.6) + vec3(0.05, 0.14, 0.01) + vec3(0.7, 1.6, 0.35) * heart;
            a = max(a, heart * 0.9);
          }
          if (a < 0.015) discard;
          gl_FragColor = vec4(c * fogKeep(), a);
        }`,
    }));
    this.mesh.name = 'elementalProjectiles'; this.mesh.renderOrder = 9;
    this.mesh.visible = false; this.mesh.frustumCulled = false;
  }

  add(kind: Kind, level: number, pos: THREE.Vector3, velocity: THREE.Vector3, scale = 1) {
    const slot = this.free.pop();
    if (slot === undefined) return -1;
    this.active[slot] = 1; this.count = Math.max(this.count, slot + 1);
    this.style.setXYZW(slot, (0.26 + level * 0.02) * scale, KINDS.indexOf(kind), fxUniforms.uTime.value, level);
    this.styling.mark(slot); this.set(slot, pos, velocity);
    return slot;
  }

  set(slot: number, pos: THREE.Vector3, velocity: THREE.Vector3) {
    if (slot < 0 || !this.active[slot]) return;
    this.position.setXYZW(slot, pos.x, pos.y, pos.z, 1);
    this.motion.setXYZW(slot, velocity.x, velocity.y, velocity.z, 0);
    this.moving.mark(slot);
  }

  remove(slot: number) {
    if (slot < 0 || !this.active[slot]) return;
    this.active[slot] = 0; this.position.setW(slot, 0); this.moving.mark(slot);
    this.free.push(slot);
    while (this.count > 0 && !this.active[this.count - 1]) this.count--;
  }

  flush() {
    (this.mesh.geometry as THREE.InstancedBufferGeometry).instanceCount = this.count;
    this.mesh.visible = this.count > 0;
    this.moving.flush([this.position, this.motion]); this.styling.flush([this.style]);
  }
}
